import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

const MONTHS = [
  "Januar",
  "Februar",
  "März",
  "April",
  "Mai",
  "Juni",
  "Juli",
  "August",
  "September",
  "Oktober",
  "November",
  "Dezember",
];

const ITEM_HEIGHT = 44;

function range(from: number, to: number): number[] {
  const step = from <= to ? 1 : -1;
  const arr: number[] = [];
  for (let i = from; step > 0 ? i <= to : i >= to; i += step) arr.push(i);
  return arr;
}

function daysInMonth(year: number, month1to12: number): number {
  return new Date(year, month1to12, 0).getDate();
}

interface WheelProps {
  items: string[];
  value: number; // index
  onChange: (index: number) => void;
  ariaLabel: string;
}

function Wheel({ items, value, onChange, ariaLabel }: WheelProps) {
  const ref = useRef<HTMLUListElement>(null);
  const programmatic = useRef(false);
  const idleTimer = useRef<number | undefined>(undefined);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    programmatic.current = true;
    el.scrollTo({ top: value * ITEM_HEIGHT });
    const t = window.setTimeout(() => {
      programmatic.current = false;
    }, 50);
    return () => window.clearTimeout(t);
  }, [value]);

  function handleScroll() {
    const el = ref.current;
    if (!el || programmatic.current) return;
    window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => {
      const idx = Math.round(el.scrollTop / ITEM_HEIGHT);
      const clamped = Math.max(0, Math.min(items.length - 1, idx));
      if (clamped !== value) onChange(clamped);
      // snap
      el.scrollTo({ top: clamped * ITEM_HEIGHT, behavior: "smooth" });
    }, 90);
  }

  return (
    <div
      className="relative h-[220px] w-full select-none overflow-hidden"
      role="group"
      aria-label={ariaLabel}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-[88px] bg-gradient-to-b from-white via-white/80 to-transparent" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[88px] bg-gradient-to-t from-white via-white/80 to-transparent" />
      <div className="pointer-events-none absolute inset-x-2 top-1/2 z-10 h-[44px] -translate-y-1/2 rounded-xl bg-brand-50/60" />
      <ul
        ref={ref}
        onScroll={handleScroll}
        className="h-full snap-y snap-mandatory overflow-y-scroll scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <li className="h-[88px]" aria-hidden="true" />
        {items.map((item, i) => (
          <li
            key={`${item}-${i}`}
            className={cn(
              "flex h-[44px] snap-center items-center justify-center tabular-nums transition",
              i === value
                ? "text-lg font-bold text-ink-900"
                : "text-base font-medium text-ink-300",
            )}
          >
            {item}
          </li>
        ))}
        <li className="h-[88px]" aria-hidden="true" />
      </ul>
    </div>
  );
}

interface DateWheelPickerProps {
  /** ISO-Datum (YYYY-MM-DD) oder leer */
  value: string;
  onChange: (iso: string) => void;
  minAge?: number;
  maxAge?: number;
}

export function DateWheelPicker({
  value,
  onChange,
  minAge = 18,
  maxAge = 90,
}: DateWheelPickerProps) {
  const today = new Date();
  const currentYear = today.getFullYear();
  const yearMax = currentYear - minAge;
  const yearMin = currentYear - maxAge;

  const parsed = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : "";
  const initial = parsed
    ? { d: Number(parsed.slice(8, 10)), m: Number(parsed.slice(5, 7)), y: Number(parsed.slice(0, 4)) }
    : { d: 15, m: 6, y: yearMax - 10 };

  const [day, setDay] = useState(initial.d);
  const [month, setMonth] = useState(initial.m);
  const [year, setYear] = useState(initial.y);

  const years = range(yearMax, yearMin); // neueste oben
  const months = MONTHS.map((_, i) => i + 1);
  const days = range(1, daysInMonth(year, month));

  useEffect(() => {
    const maxDay = daysInMonth(year, month);
    const d = Math.min(day, maxDay);
    const pad = (n: number) => String(n).padStart(2, "0");
    const iso = `${year}-${pad(month)}-${pad(d)}`;
    if (d !== day) setDay(d);
    if (iso !== value) onChange(iso);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day, month, year]);

  const yearIndex = years.indexOf(year);
  const monthIndex = months.indexOf(month);
  const dayIndex = days.indexOf(day);

  return (
    <div className="rounded-2xl border border-border bg-white p-2">
      <div className="grid grid-cols-3 gap-1">
        <Wheel
          items={days.map((d) => String(d))}
          value={dayIndex === -1 ? 0 : dayIndex}
          onChange={(i) => setDay(days[i] ?? 1)}
          ariaLabel="Tag"
        />
        <Wheel
          items={MONTHS}
          value={monthIndex === -1 ? 0 : monthIndex}
          onChange={(i) => setMonth(months[i] ?? 1)}
          ariaLabel="Monat"
        />
        <Wheel
          items={years.map((y) => String(y))}
          value={yearIndex === -1 ? 0 : yearIndex}
          onChange={(i) => setYear(years[i] ?? yearMax)}
          ariaLabel="Jahr"
        />
      </div>
    </div>
  );
}
